/**
 * Newsletter PDF Export Utility
 * 
 * This implementation uses jsPDF with html2canvas for better PDF generation
 * with a special focus on preserving clickable links.
 */

import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

import type { ReferentMutation } from '../@types/Company';

// Newsletter interface for PDF export compatibility
export interface NewsletterForPDF {
  id: string;
  subject: string;
  content: string;
  status: string;
  createdAt: string;
}

// Set to false to disable highlighting link areas in the PDF for debugging
const DEBUG_HIGHLIGHT_LINKS = false;

export const exportNewsletterToPDF = async(
  newsletter: NewsletterForPDF,
): Promise<void> => {
  try {
    // Get the newsletter content element
    const element = document.getElementById('newsletter-content');
    if (!element) {
      throw new Error('Newsletter content element not found');
    }

    // Find the actual content container (the A4 sized div)
    // For fixed newsletters, this will be directly the newsletter-content div
    // For structured newsletters, this will be the A4 container inside it
    let contentContainer: HTMLElement;
    
    // For fixed newsletters, get the newsletter-content div directly
    contentContainer = element.querySelector('.newsletter-content') as HTMLElement;
    if (!contentContainer) {
      throw new Error('Fixed newsletter content container not found');
    }
      
    // Make sure it has a proper container for PDF rendering
    if (!contentContainer.parentElement?.classList.contains('newsletter-pages-container')) {
      // If the content is not already in the proper container, wrap it
      const wrapper = document.createElement('div');
      wrapper.className = 'newsletter-pages-container';
      wrapper.style.cssText = 'width: 210mm; margin: 0 auto; background-color: white;';
      contentContainer.parentElement?.insertBefore(wrapper, contentContainer);
      wrapper.appendChild(contentContainer);
        
      // Update the content container to its parent (the wrapper we just created)
      contentContainer = wrapper;
    }

    // Reset any scaling that might be applied for preview
    const originalTransform = contentContainer.style.transform;
    contentContainer.style.transform = 'scale(1)';
    
    // Create a filename for the PDF
    let filename = 'newsletter';
    if (typeof newsletter.id === 'string' && newsletter.id.startsWith('fixed:')) {
      // For fixed newsletters, format the filename from the ID
      const [, date, slug] = newsletter.id.split(':');
      filename = `newsletter-fixed-${date}-${slug}`;
    } else {
      filename = `newsletter-${newsletter.id}-${new Date().toISOString().split('T')[0]}`;
    }
    filename += '.pdf';
    
    // Initialize PDF document with A4 dimensions
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
      compress: true,
    });
    
    // Set PDF metadata
    pdf.setProperties({
      title: `Newsletter ${newsletter.id}`,
      subject: newsletter.subject || 'Newsletter',
      creator: 'COPRAVIE',
      author: 'COPRAVIE',
    });

    // Apply temporary style fixes to avoid shifted text in PDF generation
    // Store original body line-height and set it to initial
    const originalBodyLineHeight = document.body.style.lineHeight;
    // document.body.style.lineHeight = 'initial';

    // Store original styles for images and set display to inline-block
    const originalImageStyles: Array<{ element: HTMLElement, style: string }> = [];
    const images = contentContainer.querySelectorAll('img');
    images.forEach((image) => {
      originalImageStyles.push({ element: image as HTMLElement, style: image.style.cssText });
      (image as HTMLElement).style.display = 'inline-block';
    });

    // Before first html2canvas call (measureCanvas)
    const styleForMeasure = document.createElement('style');
    styleForMeasure.textContent = 'body > div:last-child img { display: inline-block !important; }';
    document.head.appendChild(styleForMeasure);

    // First render the content to canvas to get the final rendered dimensions
    const measureCanvas = await html2canvas(contentContainer, {
      scale: 2,
      useCORS: true,
      allowTaint: true,
      logging: false,
      scrollX: 0,
      scrollY: 0,
    });

    // Remove the temporary style for measure
    styleForMeasure.remove();

    // Calculate PDF dimensions
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();
    
    // Calculate scaling factors based on the rendered canvas
    const scaleX = pdfWidth / measureCanvas.width;
    const scaleY = scaleX; // Keep aspect ratio
    
    // Store original styles of links to restore later
    const originalLinkStyles = [];
    
    // Get all links
    const links = [];
    const allLinks = contentContainer.querySelectorAll('a[href]');
    
    // Process all links - highlight them for debugging
    allLinks.forEach(link => {
      const href = link.getAttribute('href');
      if (!href) {return;}
      
      const linkElement = link as HTMLElement;
      
      // Store original style
      originalLinkStyles.push({
        element: linkElement,
        style: linkElement.style.cssText,
      });
      
      // Highlight links for debugging
      if (DEBUG_HIGHLIGHT_LINKS) {
        linkElement.style.border = '2px solid red';
        linkElement.style.backgroundColor = 'yellow';
        linkElement.style.padding = '2px';
      }
    });
    
    // Before second html2canvas call (for main canvas with links) add temporary style override
    const styleForCanvas = document.createElement('style');
    styleForCanvas.textContent = 'body > div:last-child img { display: inline-block !important; }';
    document.head.appendChild(styleForCanvas);

    // Render the content again with highlighted links
    const canvas = await html2canvas(contentContainer, {
      scale: 2,
      useCORS: true,
      allowTaint: true,
      logging: false,
      scrollX: 0,
      scrollY: 0,
    });

    // Remove the temporary style for canvas
    styleForCanvas.remove();
    
    // Now collect link positions from the rendered content
    // Process all links again to get their positions in the rendered canvas
    allLinks.forEach(link => {
      const href = link.getAttribute('href');
      if (!href) {return;}
      
      const linkElement = link as HTMLElement;
      const rect = linkElement.getBoundingClientRect();
      
      // Get the content container's position
      const containerRect = contentContainer.getBoundingClientRect();
      
      // Calculate position relative to the container
      const relativeX = rect.left - containerRect.left;
      const relativeY = rect.top - containerRect.top;
      
      // Calculate the position as a percentage of the container
      const percentX = relativeX / containerRect.width;
      const percentY = relativeY / containerRect.height;
      
      // Calculate the position in the canvas
      const canvasX = percentX * canvas.width;
      const canvasY = percentY * canvas.height;
      
      // Add a small padding to link areas to make them easier to click
      const padding = 3; // mm
      links.push({
        x: canvasX,
        y: canvasY,
        width: ((rect.width / containerRect.width) * canvas.width) + (padding / scaleX),
        height: ((rect.height / containerRect.height) * canvas.height) + (padding / scaleY),
        url: href,
      });
    });
    
    // Restore original link styles
    originalLinkStyles.forEach(item => {
      item.element.style.cssText = item.style;
    });

    // Restore original image styles
    originalImageStyles.forEach(item => {
      item.element.style.cssText = item.style;
    });

    // Restore original body line-height
    document.body.style.lineHeight = originalBodyLineHeight;

    // Calculate how many pages we need
    // Add a small buffer (0.98) to prevent creating an empty page due to minimal overflow
    const totalPages = Math.ceil((canvas.height * scaleY * 0.98) / pdfHeight);
    
    // Create a temporary canvas for each page
    for (let i = 0; i < totalPages; i++) {
      // Add a new page after the first page
      if (i > 0) {
        pdf.addPage();
      }
      
      // Calculate what part of the canvas to use for this page
      const sourceY = i * (pdfHeight / scaleY);
      const sourceHeight = Math.min(pdfHeight / scaleY, canvas.height - sourceY);
      
      // Create a temporary canvas for this page
      const tempCanvas = document.createElement('canvas');
      tempCanvas.width = canvas.width;
      tempCanvas.height = sourceHeight;
      
      // Draw the portion of the original canvas onto the temporary canvas
      const ctx = tempCanvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(
          canvas,
          0, sourceY, canvas.width, sourceHeight,
          0, 0, canvas.width, sourceHeight,
        );
      }
      
      // Add the image for this page
      const imgData = tempCanvas.toDataURL('image/jpeg', 1.0);
      pdf.addImage(imgData, 'JPEG', 0, 0, pdfWidth, sourceHeight * scaleY);
      
      // Add links for this page
      links.forEach(link => {
        // Calculate if this link is on the current page
        const linkTop = link.y * scaleY;
        const linkBottom = (link.y + link.height) * scaleY;
        const pageTop = i * pdfHeight;
        const pageBottom = (i + 1) * pdfHeight;
        
        // Check if the link is visible on this page
        if (linkBottom > pageTop && linkTop < pageBottom) {
          // Calculate the position on this page
          const linkX = link.x * scaleX;
          const linkY = (link.y * scaleY) - pageTop;
          const linkWidth = Math.max(link.width * scaleX, 10); // Ensure minimum clickable width
          const linkHeight = Math.max(link.height * scaleY, 10); // Ensure minimum clickable height
          
          // Only add the link if it's actually visible on this page
          if (linkY + linkHeight > 0 && linkY < pdfHeight) {
            // Calculate final link position with bounds checking
            const finalX = Math.max(0, linkX);
            const finalY = Math.max(0, linkY);
            const finalWidth = Math.min(linkWidth, pdfWidth - finalX);
            const finalHeight = Math.min(linkHeight, pdfHeight - finalY);
            
            // Debug: highlight link areas in the PDF
            if (DEBUG_HIGHLIGHT_LINKS) {
              pdf.setDrawColor(255, 0, 0); // Red
              pdf.setFillColor(255, 255, 0, 0.3); // Yellow with transparency
              pdf.rect(finalX, finalY, finalWidth, finalHeight, 'FD'); // Fill and Draw
            }
            
            // Add the link annotation
            pdf.link(
              finalX, 
              finalY, 
              finalWidth, 
              finalHeight, 
              { url: link.url },
            );
          }
        }
      });
    }
    
    // Save the PDF
    pdf.save(filename);
    
    // Restore original transform
    contentContainer.style.transform = originalTransform;
  } catch (error) {
    console.error('Error generating PDF :', error);
    throw new Error(`Failed to generate PDF: ${error instanceof Error ? error.message : String(error)}`);
  }
};

export const renderNewsletterToPDFBlob = async(
  newsletter: NewsletterForPDF,
): Promise<{ blob: Blob; filename: string }> => {
  // Same rendering logic as exportNewsletterToPDF, but returns a Blob instead of triggering a download
  // Intentionally duplicated to avoid risk of breaking existing behavior
  // Consider refactoring if further PDF features are added
  try {
    const element = document.getElementById('newsletter-content');
    if (!element) {
      throw new Error('Newsletter content element not found');
    }

    let contentContainer: HTMLElement;
    contentContainer = element.querySelector('.newsletter-content') as HTMLElement;
    if (!contentContainer) {
      throw new Error('Fixed newsletter content container not found');
    }

    if (!contentContainer.parentElement?.classList.contains('newsletter-pages-container')) {
      const wrapper = document.createElement('div');
      wrapper.className = 'newsletter-pages-container';
      wrapper.style.cssText = 'width: 210mm; margin: 0 auto; background-color: white;';
      contentContainer.parentElement?.insertBefore(wrapper, contentContainer);
      wrapper.appendChild(contentContainer);
      contentContainer = wrapper;
    }

    const originalTransform = contentContainer.style.transform;
    contentContainer.style.transform = 'scale(1)';

    let filename = 'newsletter';
    if (typeof newsletter.id === 'string' && newsletter.id.startsWith('fixed:')) {
      const [, date, slug] = newsletter.id.split(':');
      filename = `newsletter-fixed-${date}-${slug}`;
    } else {
      filename = `newsletter-${newsletter.id}-${new Date().toISOString().split('T')[0]}`;
    }
    filename += '.pdf';

    const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4', compress: true });
    pdf.setProperties({
      title: `Newsletter ${newsletter.id}`,
      subject: newsletter.subject || 'Newsletter',
      creator: 'COPRAVIE',
      author: 'COPRAVIE',
    });

    const originalBodyLineHeight = document.body.style.lineHeight;

    const originalImageStyles: Array<{ element: HTMLElement, style: string }> = [];
    const images = contentContainer.querySelectorAll('img');
    images.forEach((image) => {
      originalImageStyles.push({ element: image as HTMLElement, style: image.style.cssText });
      (image as HTMLElement).style.display = 'inline-block';
    });

    const styleForMeasure = document.createElement('style');
    styleForMeasure.textContent = 'body > div:last-child img { display: inline-block !important; }';
    document.head.appendChild(styleForMeasure);

    const measureCanvas = await html2canvas(contentContainer, {
      scale: 2,
      useCORS: true,
      allowTaint: true,
      logging: false,
      scrollX: 0,
      scrollY: 0,
    });

    styleForMeasure.remove();

    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();
    const scaleX = pdfWidth / measureCanvas.width;
    const scaleY = scaleX;

    const originalLinkStyles: Array<{ element: HTMLElement, style: string }> = [];
    const links: Array<{ x: number; y: number; width: number; height: number; url: string }> = [];
    const allLinks = contentContainer.querySelectorAll('a[href]');
    allLinks.forEach(link => {
      const href = link.getAttribute('href');
      if (!href) { return; }
      const linkElement = link as HTMLElement;
      originalLinkStyles.push({ element: linkElement, style: linkElement.style.cssText });
      if (DEBUG_HIGHLIGHT_LINKS) {
        linkElement.style.border = '2px solid red';
        linkElement.style.backgroundColor = 'yellow';
        linkElement.style.padding = '2px';
      }
    });

    const styleForCanvas = document.createElement('style');
    styleForCanvas.textContent = 'body > div:last-child img { display: inline-block !important; }';
    document.head.appendChild(styleForCanvas);

    const canvas = await html2canvas(contentContainer, {
      scale: 2,
      useCORS: true,
      allowTaint: true,
      logging: false,
      scrollX: 0,
      scrollY: 0,
    });

    styleForCanvas.remove();

    allLinks.forEach(link => {
      const href = link.getAttribute('href');
      if (!href) { return; }
      const linkElement = link as HTMLElement;
      const rect = linkElement.getBoundingClientRect();
      const containerRect = contentContainer.getBoundingClientRect();
      const relativeX = rect.left - containerRect.left;
      const relativeY = rect.top - containerRect.top;
      const percentX = relativeX / containerRect.width;
      const percentY = relativeY / containerRect.height;
      const canvasX = percentX * canvas.width;
      const canvasY = percentY * canvas.height;
      const padding = 3;
      links.push({
        x: canvasX,
        y: canvasY,
        width: ((rect.width / containerRect.width) * canvas.width) + (padding / scaleX),
        height: ((rect.height / containerRect.height) * canvas.height) + (padding / scaleY),
        url: href,
      });
    });

    originalLinkStyles.forEach(item => { item.element.style.cssText = item.style; });
    originalImageStyles.forEach(item => { item.element.style.cssText = item.style; });
    document.body.style.lineHeight = originalBodyLineHeight;

    const totalPages = Math.ceil((canvas.height * scaleY * 0.98) / pdfHeight);
    for (let i = 0; i < totalPages; i++) {
      if (i > 0) { pdf.addPage(); }
      const sourceY = i * (pdfHeight / scaleY);
      const sourceHeight = Math.min(pdfHeight / scaleY, canvas.height - sourceY);
      const tempCanvas = document.createElement('canvas');
      tempCanvas.width = canvas.width;
      tempCanvas.height = sourceHeight;
      const ctx = tempCanvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(canvas, 0, sourceY, canvas.width, sourceHeight, 0, 0, canvas.width, sourceHeight);
      }
      const imgData = tempCanvas.toDataURL('image/jpeg', 1.0);
      pdf.addImage(imgData, 'JPEG', 0, 0, pdfWidth, sourceHeight * scaleY);
      links.forEach(link => {
        const linkTop = link.y * scaleY;
        const linkBottom = (link.y + link.height) * scaleY;
        const pageTop = i * pdfHeight;
        const pageBottom = (i + 1) * pdfHeight;
        if (linkBottom > pageTop && linkTop < pageBottom) {
          const linkX = link.x * scaleX;
          const linkY = (link.y * scaleY) - pageTop;
          const linkWidth = Math.max(link.width * scaleX, 10);
          const linkHeight = Math.max(link.height * scaleY, 10);
          if (linkY + linkHeight > 0 && linkY < pdfHeight) {
            const finalX = Math.max(0, linkX);
            const finalY = Math.max(0, linkY);
            const finalWidth = Math.min(linkWidth, pdfWidth - finalX);
            const finalHeight = Math.min(linkHeight, pdfHeight - finalY);
            if (DEBUG_HIGHLIGHT_LINKS) {
              pdf.setDrawColor(255, 0, 0);
              pdf.setFillColor(255, 255, 0, 0.3);
              pdf.rect(finalX, finalY, finalWidth, finalHeight, 'FD');
            }
            pdf.link(finalX, finalY, finalWidth, finalHeight, { url: link.url });
          }
        }
      });
    }

    const blob = pdf.output('blob');
    contentContainer.style.transform = originalTransform;
    return { blob, filename };
  } catch (error) {
    console.error('Error generating PDF blob :', error);
    throw new Error(`Failed to generate PDF blob: ${error instanceof Error ? error.message : String(error)}`);
  }
};

/**
 * Render a newsletter PDF for a specific referent.
 *
 * Instead of creating a detached off-screen container (which loses inherited
 * styles and causes layout differences), this function temporarily swaps the
 * innerHTML of the live on-screen `.newsletter-content` element inside
 * `#newsletter-content`, renders from that identical DOM context, then restores
 * the original content. This guarantees pixel-identical output to the
 * single-referent `renderNewsletterToPDFBlob` path.
 *
 * IMPORTANT: Must NOT be called concurrently — use
 * renderNewsletterPDFsForReferents() for sequential multi-referent generation.
 */
export const renderNewsletterToPDFBlobForReferent = async(
  newsletter: NewsletterForPDF,
  referent: ReferentMutation,
): Promise<{ blob: Blob; filename: string }> => {
  // Locate the live on-screen newsletter container (same element used by
  // the single-referent renderNewsletterToPDFBlob path)
  const element = document.getElementById('newsletter-content');
  if (!element) {
    throw new Error('Newsletter content element not found');
  }

  let contentContainer = element.querySelector('.newsletter-content') as HTMLElement;
  if (!contentContainer) {
    throw new Error('Fixed newsletter content container not found');
  }

  // Ensure the container is wrapped in .newsletter-pages-container
  if (!contentContainer.parentElement?.classList.contains('newsletter-pages-container')) {
    const wrapper = document.createElement('div');
    wrapper.className = 'newsletter-pages-container';
    wrapper.style.cssText = 'width: 210mm; margin: 0 auto; background-color: white;';
    contentContainer.parentElement?.insertBefore(wrapper, contentContainer);
    wrapper.appendChild(contentContainer);
    contentContainer = wrapper;
  }

  // Save the current content so we can restore it after rendering
  const originalInnerHTML = contentContainer.querySelector('.newsletter-content')?.innerHTML
    ?? contentContainer.innerHTML;
  const newsletterContentEl = contentContainer.querySelector('.newsletter-content') as HTMLElement | null;
  const targetEl = newsletterContentEl ?? contentContainer;

  // Swap in the referent-specific content
  targetEl.innerHTML = newsletter.content;

  // Save and reset transform for rendering at scale(1)
  const originalTransform = contentContainer.style.transform;
  contentContainer.style.transform = 'scale(1)';

  try {
    // Generate filename with referent name
    const sanitizedName = `${referent.firstname}-${referent.lastname}`
      .toLowerCase()
      .replaceAll(/[^a-z0-9-]/g, '-')
      .replaceAll(/-+/g, '-');

    let filename = 'newsletter';
    if (typeof newsletter.id === 'string' && newsletter.id.startsWith('fixed:')) {
      const [, date, slug] = newsletter.id.split(':');
      filename = `newsletter-${sanitizedName}-${date}-${slug}`;
    } else {
      filename = `newsletter-${sanitizedName}-${new Date().toISOString().split('T')[0]}`;
    }
    filename += '.pdf';

    const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4', compress: true });
    pdf.setProperties({
      title: `Newsletter - ${referent.firstname} ${referent.lastname}`,
      subject: newsletter.subject || 'Newsletter',
      creator: 'COPRAVIE',
      author: 'COPRAVIE',
    });

    const originalBodyLineHeight = document.body.style.lineHeight;

    const originalImageStyles: Array<{ element: HTMLElement; style: string }> = [];
    const images = contentContainer.querySelectorAll('img');
    images.forEach((image) => {
      originalImageStyles.push({ element: image as HTMLElement, style: image.style.cssText });
      (image as HTMLElement).style.display = 'inline-block';
    });

    const styleForMeasure = document.createElement('style');
    styleForMeasure.textContent = 'body > div:last-child img { display: inline-block !important; }';
    document.head.appendChild(styleForMeasure);

    const measureCanvas = await html2canvas(contentContainer, {
      scale: 2,
      useCORS: true,
      allowTaint: true,
      logging: false,
      scrollX: 0,
      scrollY: 0,
    });

    styleForMeasure.remove();

    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();
    const scaleX = pdfWidth / measureCanvas.width;
    const scaleY = scaleX;

    const originalLinkStyles: Array<{ element: HTMLElement; style: string }> = [];
    const links: Array<{ x: number; y: number; width: number; height: number; url: string }> = [];
    const allLinks = contentContainer.querySelectorAll('a[href]');
    allLinks.forEach(link => {
      const href = link.getAttribute('href');
      if (!href) { return; }
      const linkElement = link as HTMLElement;
      originalLinkStyles.push({ element: linkElement, style: linkElement.style.cssText });
      if (DEBUG_HIGHLIGHT_LINKS) {
        linkElement.style.border = '2px solid red';
        linkElement.style.backgroundColor = 'yellow';
        linkElement.style.padding = '2px';
      }
    });

    const styleForCanvas = document.createElement('style');
    styleForCanvas.textContent = 'body > div:last-child img { display: inline-block !important; }';
    document.head.appendChild(styleForCanvas);

    const canvas = await html2canvas(contentContainer, {
      scale: 2,
      useCORS: true,
      allowTaint: true,
      logging: false,
      scrollX: 0,
      scrollY: 0,
    });

    styleForCanvas.remove();

    allLinks.forEach(link => {
      const href = link.getAttribute('href');
      if (!href) { return; }
      const linkElement = link as HTMLElement;
      const rect = linkElement.getBoundingClientRect();
      const containerRect = contentContainer.getBoundingClientRect();
      const relativeX = rect.left - containerRect.left;
      const relativeY = rect.top - containerRect.top;
      const percentX = relativeX / containerRect.width;
      const percentY = relativeY / containerRect.height;
      const canvasX = percentX * canvas.width;
      const canvasY = percentY * canvas.height;
      const padding = 3;
      links.push({
        x: canvasX,
        y: canvasY,
        width: ((rect.width / containerRect.width) * canvas.width) + (padding / scaleX),
        height: ((rect.height / containerRect.height) * canvas.height) + (padding / scaleY),
        url: href.startsWith('/') ? window.location.origin + href : href,
      });
    });

    originalLinkStyles.forEach(item => { item.element.style.cssText = item.style; });
    originalImageStyles.forEach(item => { item.element.style.cssText = item.style; });
    document.body.style.lineHeight = originalBodyLineHeight;

    const totalPages = Math.ceil((canvas.height * scaleY * 0.98) / pdfHeight);
    for (let i = 0; i < totalPages; i++) {
      if (i > 0) { pdf.addPage(); }
      const sourceY = i * (pdfHeight / scaleY);
      const sourceHeight = Math.min(pdfHeight / scaleY, canvas.height - sourceY);
      const tempCanvas = document.createElement('canvas');
      tempCanvas.width = canvas.width;
      tempCanvas.height = sourceHeight;
      const ctx = tempCanvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(canvas, 0, sourceY, canvas.width, sourceHeight, 0, 0, canvas.width, sourceHeight);
      }
      const imgData = tempCanvas.toDataURL('image/jpeg', 1.0);
      pdf.addImage(imgData, 'JPEG', 0, 0, pdfWidth, sourceHeight * scaleY);
      links.forEach(link => {
        const linkTop = link.y * scaleY;
        const linkBottom = (link.y + link.height) * scaleY;
        const pageTop = i * pdfHeight;
        const pageBottom = (i + 1) * pdfHeight;
        if (linkBottom > pageTop && linkTop < pageBottom) {
          const linkX = link.x * scaleX;
          const linkY = (link.y * scaleY) - pageTop;
          const linkWidth = Math.max(link.width * scaleX, 10);
          const linkHeight = Math.max(link.height * scaleY, 10);
          if (linkY + linkHeight > 0 && linkY < pdfHeight) {
            const finalX = Math.max(0, linkX);
            const finalY = Math.max(0, linkY);
            const finalWidth = Math.min(linkWidth, pdfWidth - finalX);
            const finalHeight = Math.min(linkHeight, pdfHeight - finalY);
            if (DEBUG_HIGHLIGHT_LINKS) {
              pdf.setDrawColor(255, 0, 0);
              pdf.setFillColor(255, 255, 0, 0.3);
              pdf.rect(finalX, finalY, finalWidth, finalHeight, 'FD');
            }
            pdf.link(finalX, finalY, finalWidth, finalHeight, { url: link.url });
          }
        }
      });
    }

    const blob = pdf.output('blob');
    return { blob, filename };
  } finally {
    // Restore the original content and transform
    targetEl.innerHTML = originalInnerHTML;
    contentContainer.style.transform = originalTransform;
  }
};

/**
 * Generate PDFs for multiple referents sequentially.
 *
 * html2canvas captures DOM state globally, so concurrent calls interfere with
 * each other (duplicate IDs, shared style tags, layout shifts during cleanup).
 * This helper processes referents one at a time to guarantee correct output.
 *
 * It also deduplicates filenames (appending an index) so that referents sharing
 * the same firstname/lastname don't silently overwrite each other in the zip.
 */
export const renderNewsletterPDFsForReferents = async(
  newsletters: Array<{ newsletter: NewsletterForPDF; referent: ReferentMutation }>,
): Promise<Array<{ blob: Blob; filename: string }>> => {
  const results: Array<{ blob: Blob; filename: string }> = [];
  const usedFilenames = new Set<string>();

  for (let idx = 0; idx < newsletters.length; idx++) {
    const { newsletter, referent } = newsletters[idx];
    try {
      // eslint-disable-next-line no-await-in-loop -- Sequential execution is intentional: html2canvas swaps the live DOM content
      const result = await renderNewsletterToPDFBlobForReferent(newsletter, referent);

      // Deduplicate filenames to avoid silent overwrites in the zip
      let { filename } = result;
      if (usedFilenames.has(filename)) {
        const ext = filename.lastIndexOf('.');
        const base = ext > 0 ? filename.slice(0, ext) : filename;
        const extension = ext > 0 ? filename.slice(ext) : '.pdf';
        filename = `${base}-${idx + 1}${extension}`;
      }
      usedFilenames.add(filename);

      results.push({ blob: result.blob, filename });
    } catch (error) {
      console.error(`[PDF] Failed to generate PDF for referent ${referent.firstname} ${referent.lastname}:`, error);
      // Continue with remaining referents instead of aborting everything
    }
  }

  if (results.length === 0 && newsletters.length > 0) {
    throw new Error('All PDF generations failed. Check the browser console for details.');
  }

  return results;
};
